// cytologyResultsMixin.js
import moment from "moment";
import { format, parseISO } from "date-fns";
import { mapState, mapGetters } from "vuex";
import { CytResultTypeEnum, CytReviewTypeEnum, CaseStatusEnum } from "../modules/enums";
import cytologyResultService from "../services/cytresults";
import { CYTOLOGY_SESSION_PROMPT, fromBusEvent } from "../modules/eventBus";
import { tap } from "rxjs/operators";
import { cloneDeep } from "lodash";

export default {
  data() {
    return {
      reviewAgree: false,
      agreesWithCytResultTypeId: null,
      // Result collections
      screenings: [],
      reviews: [],
      pathologistReviews: [],
      agreeableResults: [],
      agreesWithPopup: false,
      // Latest detailed results
      lastScreening: null,
      lastReview: null,
      lastPathologistReview: null,
      forcedReview: CytReviewTypeEnum.None,
      defaultResult: {
        cytResultTypeId: CytResultTypeEnum.Screening,
        resultedLabLocationId: null,
        resultedBy: "",
        resultedOn: new Date(),
        agreesWithPrevious: true,
        cytReviewTypeId: CytReviewTypeEnum.None,
        cytReviewTypeRequestedId: CytReviewTypeEnum.None,
        cytDiagnosticMacroId: null,
        macroIsModified: false,
        diagCodes: []
      },
      // Current state
      currentResult: {
        cytResultTypeId: CytResultTypeEnum.Screening,
        resultedLabLocationId: null,
        resultedBy: "",
        resultedOn: new Date(),
        agreesWithPrevious: true,
        cytReviewTypeId: CytReviewTypeEnum.None,
        cytReviewTypeRequestedId: CytReviewTypeEnum.None,
        cytDiagnosticMacroId: null,
        macroIsModified: false,
        diagCodes: []
      },
      currentPreviewResult: null,
      initialType: null,

      // UI state flags
      isScreening: false,
      isReviewing: false,
      isPreviewOpen: false,
      isSystemMessageOpen: false,
      enabled: true
    };
  },
  created() {
    if (!this.hasCytologyPrompted) {
      this.initializePrompt();
    } else {
      this.loadCytResults().then(data => {
        this.handleLoadSession(data);
      });
    }
  },
  subscriptions() {
    return {
      promptEvent: fromBusEvent(CYTOLOGY_SESSION_PROMPT).pipe(tap(t => this.handleLoadSession(t)))
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      labSettings: state => state.labSettings,
      sessionDetails: state => state.sessionDetails,
      caseStatus: state => state.accessionStore.caseHeader?.status,
      hasCytologyPrompted: state => state.sessionDetails.cytologyPrompted,
      isSessionRescreen: state => state.sessionDetails.reScreen,
      isPromptOpen: state => state.sessionDetails.cytologyPrompt,
      sessionResultDate: state => state.sessionDetails.resultedOnDate,
      rescreenReviews: state => state.labSettings.CytologyRescreenReviews
    }),

    ...mapGetters(["cytReviewTypeId"]),

    checkUserPermissions() {
      if (!this.isCytologist) {
        this.enabled = false;
        return false;
      }
      return true;
    },
    // User role checking
    isCytologist() {
      const cytologistRoles = [
        CytReviewTypeEnum.Screener,
        CytReviewTypeEnum.Reviewer,
        CytReviewTypeEnum.Pathologist
      ];
      console.log(
        "Current User's cytology role: %s , vs roles",
        this.cytologyReviewType,
        cytologistRoles
      );
      return cytologistRoles.includes(this.cytologyReviewType);
    },

    isScreener() {
      return (
        this.cytReviewTypeId === CytReviewTypeEnum.Screener ||
        this.cytReviewTypeId === CytReviewTypeEnum.Pathologist
      );
    },

    isReviewer() {
      return (
        this.cytReviewTypeId === CytReviewTypeEnum.Reviewer ||
        this.cytReviewTypeId === CytReviewTypeEnum.Pathologist
      );
    },

    isPathologist() {
      return this.cytReviewTypeId === CytReviewTypeEnum.Pathologist;
    },
    userId() {
      return this.currentUser.id;
    },
    // User identification
    username() {
      return this.currentUser?.lastName + ", " + this.currentUser?.firstName;
    },

    useremail() {
      return this.currentUser.email;
    },

    cytologyReviewType() {
      return this.cytReviewTypeId;
    },
    isWaitingForReview() {
      const awaitingStatuses = [CaseStatusEnum.CytAwaitingReviewer];
      return awaitingStatuses.includes(this.caseStatus);
    },
    // Case status checking
    isCaseComplete() {
      const completeStatuses = [
        CaseStatusEnum.Reported,
        CaseStatusEnum.ReReleased,
        CaseStatusEnum.ReportedPrelim,
        CaseStatusEnum.CytResultComplete
      ];
      if (this.isCaseScreener && this.caseStatus === CaseStatusEnum.CytResultComplete) {
        return false;
      }
      return completeStatuses.includes(this.caseStatus);
    },
    isAvailableForRescreen() {
      //! These statuses are reached only when a screening has already happened.
      const completeStatuses = [
        CaseStatusEnum.CytResultComplete,
        CaseStatusEnum.Reported,
        CaseStatusEnum.CytAwaitingPathologist,
        CaseStatusEnum.CytAwaitingReviewer
      ];
      return completeStatuses.includes(this.caseStatus);
    },

    // User-specific role checking for current case
    isCaseScreener() {
      return (
        this.lastScreening?.resultedBy === this.username ||
        this.lastScreening?.resultedBy === this.useremail
      );
    },

    isCaseReviewer() {
      return (
        this.lastReview?.resultedBy === this.username ||
        this.lastReview?.resultedBy === this.useremail
      );
    },

    // Current screener and reviewer info
    screener() {
      if (this.lastScreening) {
        return this.lastScreening.resultedBy;
      }
      if (this.isScreening && this.currentResult.cytResultTypeId === CytResultTypeEnum.Screening) {
        return this.username;
      }
      return "";
    },

    reviewer() {
      if (this.lastReview) {
        return this.lastReview.resultedBy;
      }
      if (this.currentResult.cytResultTypeId === CytResultTypeEnum.Review) {
        return this.username;
      }
      return "";
    },

    // Preview and display logic
    currentViewType() {
      if (this.currentPreviewResult === null) {
        return null;
      }
      if (this.currentPreviewResult.cytResultTypeId === CytResultTypeEnum.Screening) {
        return "Screener";
      }
      if (this.currentPreviewResult.cytResultTypeId === CytResultTypeEnum.Review) {
        return "Reviewer";
      }
      return null;
    },

    currentViewUser() {
      if (this.currentViewType === "Screener") {
        return this.screener;
      }
      if (this.currentViewType === "Reviewer") {
        return this.reviewer;
      }
      return "";
    },

    currentResultedOn() {
      if (this.currentPreviewResult === null) {
        return null;
      }
      if (this.currentPreviewResult.resultedOn) {
        return format(parseISO(this.currentPreviewResult.resultedOn), "M/dd/yyyy");
      }
      return "";
    },
    shouldStartAdditionalReview() {
      if (!this.lastReview) return false;

      const requestedReview = this.lastReview.cytReviewTypeRequestedId;
      return this.canUserReview(requestedReview);
    },
    previewText() {
      if (this.currentPreviewResult === null) {
        return null;
      }
      if (Array.isArray(this.currentPreviewResult?.cytResultDiagnosisCodes)) {
        return this.currentPreviewResult.cytResultDiagnosisCodes;
      }
      return [];
    },

    // Last result logic
    lastResult() {
      const lastScreenDate = this.lastScreening?.resultedOn;
      const lastReviewDate = this.lastReview?.resultedOn;
      if (this.lastReview) {
        if (lastScreenDate > lastReviewDate) {
          return this.lastScreening;
        }
        return this.lastReview;
      }
      return this.lastScreening;
    },

    // Review permissions
    canAddReview() {
      if (!this.lastScreening) {
        return false;
      }
      return this.isReviewer;
    },

    canReview() {
      if (this.isCaseComplete) {
        return false;
      }
      if (this.lastScreening) {
        const requestedReviewType = this.lastScreening.cytReviewTypeRequestedId;
        if (this.isReviewer && requestedReviewType === CytReviewTypeEnum.Pathologist) {
          return false;
        }
      }
      return this.isReviewer && !this.isCaseScreener;
    },

    // Form state logic
    formDisabled() {
      console.log("formDisabled check", {
        isCytRole: this.isCytologist,
        isScreening: this.isScreening,
        isCaseComplete: this.caseStatus === CaseStatusEnum.CytResultComplete,
        isReviewing: this.isReviewing
      });
      //This person does not have cytology role - disable form entirely.
      if (!this.isCytologist) {
        return true;
      }
      if (this.isScreening) {
        return false;
      }
      if (this.caseStatus === CaseStatusEnum.CytResultComplete) {
        return true;
      }
      if (this.isReviewing) {
        return false;
      }
      return true;
    },
    screeningDisallowed() {
      // user flag for disallowing screenings.
      return this.currentUser.cytDisallowScreening;
    },
    // Workflow condition computed properties (moved from methods)
    shouldStartScreening() {
      if (this.screeningDisallowed) {
        return false;
      }
      return (
        this.screenings?.length < 1 && (this.isScreener || this.isPathologist || this.isReviewer)
      );
    },

    shouldEditExistingScreening() {
      const disallowedStatus = [
        CaseStatusEnum.CytAwaitingPathologist,
        CaseStatusEnum.CytAwaitingReviewer
      ];
      if (disallowedStatus.includes(this.caseStatus)) {
        return false;
      }
      const hasNoReview = !this.reviews?.length;
      return hasNoReview && this.isCaseScreener;
    },

    shouldStartReview() {
      const hasNoReview = !this.reviews?.length;
      const hasScreening = this.lastScreening;
      const requestedReview = this.lastScreening?.cytReviewTypeRequestedId;

      return hasNoReview && hasScreening && this.canUserReview(requestedReview);
    },

    shouldEditExistingReview() {
      const hasReview = this.reviews?.length > 0;
      return hasReview && this.isCaseReviewer;
    },
    isCasePathologist() {
      if (!this.isPathologist) {
        return false;
      }
      const hasPathologistReview = this.pathologistReviews?.length > 0;
      if (hasPathologistReview) {
        const lastPathologistReview = this.pathologistReviews[0];
        return (
          lastPathologistReview.resultedBy === this.username ||
          lastPathologistReview.resultedBy === this.useremail
        );
      }
      return false;
    },
    shouldEditExistingPathologistReview() {
      const hasPathologistReview = this.pathologistReviews?.length > 0;
      return hasPathologistReview && this.isCasePathologist;
    },

    // Render logic
    renderBuilder() {
      if (this.isScreening) {
        return true;
      }
      if (this.isReviewing && !this.currentResult.agreesWithPrevious) {
        return true;
      }
      return false;
    },

    renderPreview() {
      if (this.renderBuilder) {
        return false;
      }
      if (this.isPreviewOpen && this.previewText) {
        return true;
      }
      if (this.isReviewing && this.currentResult.agreesWithPrevious) {
        return true;
      }
      return false;
    },
    targetSpecimenId() {
      return this.value?.id || this.specimen?.id;
    },

    resultDate: {
      get() {
        return this.sessionResultDate;
      },
      set(val) {
        this.$store.commit("sessionDetails/setResultedOnDate", val);
      }
    }
  },

  methods: {
    // ========================================
    // MAIN ORCHESTRATOR
    // ========================================
    handleLoadSession(data) {
      if (data) {
        this.currentResult.resultedBy = data.resultedBy;
        this.currentResult.resultedOn = data.resultedOn;
      } else {
        this.currentResult.resultedBy = this.username;
        this.currentResult.resultedOn = this.sessionResultDate;
      }

      this.agreeableResults = [
        ...(this.lastReview ? [this.lastReview] : []),
        ...(this.lastPathologistReview ? [this.lastPathologistReview] : []),
        ...(this.lastScreening ? [this.lastScreening] : [])
      ]
        .map(result => result.cytResultTypeId)
        .map(typeId => {
          const targetResultType = Object.keys(CytResultTypeEnum).find(
            key => CytResultTypeEnum[key] === typeId
          );
          return {
            id: typeId,
            displayName: targetResultType
          };
        });
      if (this.isSessionRescreen || data?.reScreen) {
        if (this.currentResult?.id) {
          delete this.currentResult.id;
        }
        this.currentResult.cytResultTypeId = CytResultTypeEnum.Rescreening;
        if (this.rescreenReviews && (this.lastReview || this.lastPathologistReview)) {
          this.agreesWithPopup = true;
        }
      }
    },
    async loadCytResults(workflowUpdate = true) {
      try {
        console.log("Loading cytology results...", workflowUpdate);
        const resultData = await this.loadResultData();
        this.assignResultData(resultData);
        await this.loadDetailedResults();
        this.determinePreviewResult();

        if (!this.checkUserPermissions) {
          return [resultData.screeningsData, resultData.reviewsData];
        }
        if (workflowUpdate) {
          this.determineWorkflowState(resultData);
        }

        return [resultData.screeningsData, resultData.reviewsData];
      } catch (error) {
        console.error("Error loading cytology results:", error);
        this.handleLoadError(error);
        throw error;
      }
    },

    // ========================================
    // DATA LOADING
    // ========================================

    async loadResultData() {
      const store = cytologyResultService.createSearch();
      if (!this.targetSpecimenId) {
        if (this.specimens && this.specimens.length === 1) {
          this.specimen = cloneDeep({
            ...this.specimens[0],
            initIcdCodes: this.specimens[0]?.icdCodes ? this.specimens[0].icdCodes : [],
            initCptCodes: this.specimens[0]?.cptCodes ? this.specimens[0].cptCodes : [],
            initResultsMacros: this.specimens[0]?.resultsMacros
              ? this.specimens[0].resultsMacros
              : []
          });
        }
      }
      const specimenFilter = ["specimenId", this.targetSpecimenId];
      const queries = {
        screenings: this.createQuery(store, CytResultTypeEnum.Screening, specimenFilter),
        latestScreening: this.createQuery(store, CytResultTypeEnum.Screening, specimenFilter, {
          latest: true
        }),
        pathologistReviews: this.createQuery(store, CytResultTypeEnum.Pathologist, specimenFilter),
        latestPathologistReview: this.createQuery(
          store,
          CytResultTypeEnum.Pathologist,
          specimenFilter,
          {
            latest: true
          }
        ),
        reviews: this.createQuery(store, CytResultTypeEnum.Review, specimenFilter),
        latestReview: this.createQuery(store, CytResultTypeEnum.Review, specimenFilter, {
          latest: true
        })
      };

      const [
        screeningsData,
        reviewsData,
        latestScreeningData,
        latestReviewData,
        pathologistReviewsData,
        latestPathologistReviewData
      ] = await Promise.all([
        queries.screenings,
        queries.reviews,
        queries.latestScreening,
        queries.latestReview,
        queries.pathologistReviews,
        queries.latestPathologistReview
      ]);

      return {
        screeningsData,
        reviewsData,
        latestScreeningData,
        latestReviewData,
        pathologistReviewsData,
        latestPathologistReviewData
      };
    },

    createQuery(store, resultTypeId, specimenFilter, options = {}) {
      let filter = [["cytResultTypeId", resultTypeId], "and", specimenFilter];
      const queryConfig = { filter };
      if (options.latest) {
        queryConfig.sort = [{ selector: "resultedOn", desc: true }];
        queryConfig.take = 1;
      }
      return store.load(queryConfig);
    },

    assignResultData(data) {
      this.screenings = data.screeningsData;
      this.reviews = data.reviewsData;
      if (data.latestReviewData?.length > 0) {
        this.lastReview = data.latestReviewData[0];
      }
      if (data.latestScreeningData?.length > 0) {
        this.lastScreening = data.latestScreeningData[0];
      }
      this.pathologistReviews = data.pathologistReviewsData;
      if (data.pathologistReviewsData?.length > 0) {
        this.lastPathologistReview = data.pathologistReviewsData[0];
      }
      if (data.latestPathologistReviewData?.length > 0) {
        this.lastPathologistReview = data.latestPathologistReviewData[0];
      }
    },

    async loadDetailedResults() {
      const detailPromises = [
        this.loadDetailedResult("lastScreening", this.screenings),
        this.loadDetailedResult("lastReview", this.reviews),
        this.loadDetailedResult("lastPathologistReview", this.pathologistReviews)
      ];

      await Promise.all(detailPromises);
    },

    async loadDetailedResult(propertyName, resultsArray) {
      try {
        if (resultsArray.length > 0) {
          const { id } = resultsArray[0];
          const data = await cytologyResultService.getOne(id);
          this[propertyName] = data;
        } else {
          this[propertyName] = null;
        }
      } catch (error) {
        console.error(`Failed to load ${propertyName}:`, error);
        this[propertyName] = null;
      }
    },

    // ========================================
    // PREVIEW LOGIC
    // ========================================

    determinePreviewResult() {
      // Priority: Pathologist Review (if newer) > Latest Review > Latest Screening

      if (this.shouldShowPathologistReview()) {
        this.currentPreviewResult = this.lastPathologistReview;
        this.isPreviewOpen = true;
      } else if (this.lastReview) {
        this.currentPreviewResult = this.lastReview;
        this.isPreviewOpen = true;
      } else if (this.lastScreening) {
        this.currentPreviewResult = this.lastScreening;
        this.isPreviewOpen = true;
      } else {
        this.isPreviewOpen = false;
      }
    },

    shouldShowPathologistReview() {
      if (!this.lastPathologistReview) return false;

      const lastReviewDate = this.lastReview?.resultedOn;
      if (!lastReviewDate) return true;

      return moment(this.lastPathologistReview.resultedOn).isAfter(moment(lastReviewDate));
    },

    // ========================================
    // PERMISSION CHECKING
    // ========================================
    // Note: Most permission checking is now in computed properties

    canUserReview(requestedReviewType = null) {
      if (!requestedReviewType) {
        return this.isReviewer || this.isPathologist;
      }

      if (requestedReviewType === CytReviewTypeEnum.Pathologist) {
        return this.isPathologist;
      }

      if (requestedReviewType === CytReviewTypeEnum.Reviewer) {
        return this.isReviewer || this.isPathologist;
      }
      return this.isReviewer || this.isPathologist;
    },

    // ========================================
    // WORKFLOW STATE DETERMINATION
    // ========================================

    determineWorkflowState() {
      console.log("Determining workflow state...", {
        isCaseComplete: this.isCaseComplete,
        isSessionRescreen: this.isSessionRescreen,
        isAvailableForRescreen: this.isAvailableForRescreen,
        shouldEditExistingReview: this.shouldEditExistingReview,
        shouldEditExistingPathologistReview: this.shouldEditExistingPathologistReview,
        isWaitingForReview: this.isWaitingForReview,
        canUserReview: this.canUserReview(null)
      });
      if (this.isCaseComplete && !this.isSessionRescreen) {
        this.setCompleteState();
        return;
      }

      if (this.shouldEditExistingReview) {
        this.setEditReviewState();
        return;
      }
      if (this.shouldEditExistingPathologistReview) {
        this.setEditPathologistReviewState();
        return;
      }

      if (this.isWaitingForReview && this.canUserReview(null)) {
        this.setReviewState();
        return;
      }
      console.log("The current session is a rescreen:", this.isSessionRescreen);
      if (this.isSessionRescreen) {
        if (this.isAvailableForRescreen) {
          this.enabled = true;
          this.currentResult = Object.assign({}, this.defaultResult);
          this.currentResult.cytResultTypeId = CytResultTypeEnum.Rescreening;
          this.currentResult.agreesWithPrevious = false;
          this.isScreening = true;
          this.isReScreening = true;
          this.isPreviewOpen = false;
          this.currentPreviewResult = [];

          return;
        }
      }

      if (this.shouldStartScreening) {
        this.setScreeningState();
        return;
      }

      if (this.shouldEditExistingScreening) {
        this.setEditScreeningState();
        return;
      }

      if (this.shouldStartReview) {
        this.setReviewState();
        return;
      }

      if (this.shouldStartAdditionalReview) {
        this.setAdditionalReviewState();
        return;
      }

      // Default: preview only
      this.setPreviewOnlyState();
    },

    // ========================================
    // WORKFLOW STATE CONDITIONS
    // ========================================
    // Note: Most workflow conditions are now computed properties
    // These methods remain for complex logic that requires parameters

    // ========================================
    // WORKFLOW STATE SETTERS
    // ========================================

    setCompleteState() {
      this.isScreening = false;
      this.isReviewing = false;
      this.isPreviewOpen = true;
      this.isSystemMessageOpen = true;
    },

    setScreeningState() {
      this.currentResult.cytResultTypeId = CytResultTypeEnum.Screening;
      this.currentResult.c;
      this.isScreening = true;
      this.$nextTick(() => {
        this.setUserFocus();
      });
    },

    setEditScreeningState() {
      this.currentResult = { ...this.lastScreening };
      this.setupEditingForm(this.lastScreening);
      this.isReviewing = false;
      this.isScreening = true;
      this.isPreviewOpen = false;
    },

    setReviewState() {
      this.currentResult.cytResultTypeId = CytResultTypeEnum.Review;
      const requestedReview = this.lastScreening?.cytReviewTypeRequestedId;

      if (requestedReview !== CytReviewTypeEnum.None) {
        this.handleAddRequestedReview(requestedReview);
      } else {
        this.handleAddReview();
      }
    },

    setEditReviewState() {
      this.currentResult = { ...this.lastReview };
      this.setupEditingForm(this.lastReview);
      this.isReviewing = true;
      this.isScreening = false;
      this.isPreviewOpen = false;
    },

    setEditPathologistReviewState() {
      const lastPathologistReview = this.lastPathologistReview;
      this.currentResult = { ...lastPathologistReview };
      this.setupEditingForm(lastPathologistReview);
      this.isReviewing = true;
      this.isScreening = false;
      this.isPreviewOpen = false;
    },

    setAdditionalReviewState() {
      const requestedReview = this.lastReview.cytReviewTypeRequestedId;
      this.handleAddRequestedReview(requestedReview);
    },

    setPreviewOnlyState() {
      this.isReviewing = false;
      this.isScreening = false;
      this.isPreviewOpen = true;
      this.isSystemMessageOpen = true;
    },

    // ========================================
    // FORM SETUP
    // ========================================

    setupEditingForm(result) {
      const { cytResultDiagnosisCodes } = result;
      const startingValues = cytResultDiagnosisCodes.map(code => ({
        cytDiagnosticCodeId: code.cytDiagnosticCodeId,
        comments: code.comments
      }));

      this.$set(this.currentResult, "diagCodes", startingValues);

      this.$nextTick(() => {
        const builder = this.$refs["resulter"];
        if (builder && builder.handleLoadPrefill) {
          builder.handleLoadPrefill(startingValues);
        }
      });
    },

    // ========================================
    // UTILITY METHODS
    // ========================================

    setUserFocus() {
      // Start in the Results Macro field unless no protocol then start int he protocol field
      if (this.specimen.protocolId) {
        const resulter = this.$refs["resulter"];
        if (resulter) {
          resulter.focusMacro();
        }
      } else {
        const protocolField = this.$refs["protocol"];
        if (protocolField) {
          protocolField.focus();
        }
      }
    },

    handleLoadError(error) {
      // Add your error handling logic here
      this.isScreening = false;
      this.isReviewing = false;
      this.isPreviewOpen = false;
      this.isSystemMessageOpen = true;

      // Could emit event for parent component to handle
      this.$emit("load-error", error);
    },

    // ========================================
    // REVIEW HANDLING METHODS
    // ========================================
    // These methods should be implemented based on your existing logic
    handleAddRequestedReview(reviewTypeId) {
      this.currentResult.cytResultTypeId = reviewTypeId;
      this.currentResult.agreesWithPrevious = true;
      this.isReviewing = true;
      this.isScreening = false;
      this.isPreviewOpen = false;
      this.handleAgree();
    },

    handleSelectCodes(data) {
      this.currentResult.diagCodes = data.diagCodes;
      this.currentResult.cytDiagnosticMacroId = data.cytDiagnosticMacroId;
      this.currentResult.macroIsModified = data.macroIsModified;
    },
    handleCancel() {
      this.isLocationPopupOpen = false;
    },
    handleCancelReview() {
      this.isReviewing = false;
      this.isScreening = false;
      this.isPreviewOpen = true;
      this.currentPreviewResult = this.lastScreening;
    },
    handleDisagree() {
      this.currentResult.diagCodes = [];
      const builder = this.$refs["resulter"];
      if (builder) {
        builder.handleLoadPrefill([]);
      }
    },
    handleAgreePopupSubmit() {
      console.log("Agree popup submitted, reviewAgree:", this.reviewAgree);
      if (this.reviewAgree) {
        this.handleAgree(this.agreesWithCytResultTypeId);
      }
      this.agreesWithPopup = false;
    },
    handleAgree(resultTypeId) {
      let lastResult = this.lastScreening;
      this.currentResult.agreesWithPrevious = true;
      if (resultTypeId) {
        const allResults = [
          ...(this.lastReview ? [this.lastReview] : []),
          ...(this.lastPathologistReview ? [this.lastPathologistReview] : []),
          ...(this.lastScreening ? [this.lastScreening] : [])
        ];
        lastResult = allResults.find(r => r.cytResultTypeId === resultTypeId);
        this.currentResult.agreesWithCytResultTypeId = resultTypeId;
      }
      if (lastResult?.cytResultDiagnosisCodes) {
        const diagCodes = lastResult.cytResultDiagnosisCodes.map(e => {
          return {
            cytDiagnosticCodeId: e.cytDiagnosticCodeId,
            comment: e.comment
          };
        });
        if (lastResult.cytDiagnosticMacroId) {
          this.currentResult.cytDiagnosticMacroId = lastResult.cytDiagnosticMacroId;
          this.currentResult.macroIsModified = lastResult.macroIsModified;
        }
        this.currentResult.diagCodes = diagCodes;
        const builder = this.$refs["resulter"];
        if (builder) {
          builder.handleLoadPrefill(diagCodes);
        }
      }
    },
    handleAddReview() {
      this.currentResult.cytResultTypeId = CytResultTypeEnum.Review;
      this.currentResult.agreesWithPrevious = true;
      this.currentResult.username = this.username;
      this.isReviewing = true;
      this.isScreening = false;
      this.isPreviewOpen = false;
      this.handleAgree();
    },
    // ========================================
    // RESET METHODS (useful for cleanup)
    // ========================================

    resetCytologyState() {
      this.screenings = [];
      this.reviews = [];
      this.pathologistReviews = [];
      this.lastScreening = null;
      this.lastReview = null;
      this.lastPathologistReview = null;
      this.currentResult = {};
      this.currentPreviewResult = null;
      this.isScreening = false;
      this.isReviewing = false;
      this.isPreviewOpen = false;
      this.isSystemMessageOpen = false;
      this.enabled = true;
    },
    togglePrompt() {
      this.$store.commit("sessionDetails/toggleCytologyPrompt");
    },
    async initializePrompt(type) {
      this.initialType = type;
      this.togglePrompt();
    }
  },
  watch: {
    cytologyPromptOpen(newVal) {
      if (!newVal) {
        this.$cytologySessionPrompt.next();
      }
    },
    value: {
      deep: true,
      handler(nv, ov) {
        if (nv?.id !== ov?.id) {
          this.loadCytResults().then(() => {
            this.handleLoadSession();
          });
        }
      }
    }
  }
};
